import config from '../../../assets/config';

// Contract shared between the dashboard's OAuth callback page
// (pages/GscOAuthCallbackPage.tsx, loaded inside the popup) and the page that
// opened it. On the return leg the callback page postMessages this to its
// opener, which continues the wizard.
export const GSC_OAUTH_MESSAGE_TYPE = 'gsc-oauth-callback';

export type GscOAuthMessage = {
  type: typeof GSC_OAUTH_MESSAGE_TYPE;
  sourceId?: string;
  sessionId?: string;
  status?: string;
  reason?: string;
  // Machine-readable counterpart of `reason`, for analytics.
  errorCode?: string;
};

// The wizard's post-OAuth state: both ids are required to enter the domain picker.
export type GscConnectionParams = { sessionId: string; sourceId: string };

// Google's OAuth redirect URI is registered as the dashboard's callback route,
// so the raw ?code&state land here first. Only visibility can exchange the
// code (it holds the client secret), so the callback page forwards the query
// to visibility's public callback endpoint. `rawSearch` must be the location's
// search string verbatim: the state is HMAC-signed and any re-encoding breaks
// its signature.
export function buildGscOAuthRelayUrl(rawSearch: string): string | undefined {
  if (!config.apiUrl.visibilityServer) {
    return undefined;
  }
  return `${config.apiUrl.visibilityServer}/oauth/gsc/callback${rawSearch}`;
}

// Visibility requests openid+email+webmasters.readonly (prerender-core).
// Google may echo `email` or `userinfo.email`.
export const GSC_REQUIRED_SCOPE = 'https://www.googleapis.com/auth/webmasters.readonly';
const GSC_EMAIL_SCOPES = ['email', 'https://www.googleapis.com/auth/userinfo.email'] as const;

// Partial grants are reachable once more than one scope is requested. Absent
// `scope` is not a refusal — Google not echoing one must not dead-end a connect.
export function isGscScopeGranted(grantedScope: string | null): boolean {
  if (grantedScope === null || grantedScope.trim() === '') {
    return true;
  }
  const granted = grantedScope.split(/\s+/);
  return (
    granted.includes(GSC_REQUIRED_SCOPE) && GSC_EMAIL_SCOPES.some((scope) => granted.includes(scope))
  );
}

// The relay leg in one place. The callback page redirects to whatever this
// answers, and the boot path (features/auth/keycloakAuth) asks the same
// question to decide whether to keep keycloak out of the leg. One derivation,
// so the two cannot disagree about which legs leave this origin.
//
// `rawSearch` must be the location's search string verbatim — see
// buildGscOAuthRelayUrl.
export function resolveGscOAuthRelayTarget(rawSearch: string): string | undefined {
  const params = new URLSearchParams(rawSearch);
  if (!params.get('code')) {
    return undefined;
  }
  if (!isGscScopeGranted(params.get('scope'))) {
    return undefined;
  }
  return buildGscOAuthRelayUrl(rawSearch);
}

// The popup legs need no session: each one relays onward, or postMessages its
// result to the opener and closes. Shared with the boot path for the same
// reason as the relay target above.
export function isGscOAuthPopupWindow(): boolean {
  return Boolean(window.opener) && window.opener !== window;
}

// What the callback route reports when it is NOT relaying — the only state the
// flow needs once Google's own params have done their job.
//
// Two callers share it, which is the point: the callback page turns it into a
// postMessage or a wizard navigation, and the boot path
// (features/auth/keycloakAuth) writes it back into the query when it must clear
// Google's OIDC params before keycloak init. One derivation, so the two cannot
// disagree about what a leg means.
export type GscCallbackOutcome = {
  status?: string;
  reason?: string;
  errorCode?: string;
  sourceId?: string;
  sessionId?: string;
};

// Call only on a leg that is not relaying: a `code` that reaches here is a code
// nobody could forward, which is why it reads as a failure.
//
// Idempotent by construction — the result carries none of the params it reads
// (`error`, `code`, `scope`), only `status`/`reason`/`error_code`, which pass
// straight through. `gscCallbackOutcomeToQuery` output therefore derives back to
// the same outcome, so a URL the boot path already rewrote survives a second
// pass unchanged.
export function deriveGscCallbackOutcome(searchParams: URLSearchParams): GscCallbackOutcome {
  let status = searchParams.get('status') ?? undefined;
  // Machine detail only — visibility's uppercase enum name or Google's error
  // slug. The wizard owns the customer-facing copy; author none here.
  let reason = searchParams.get('reason') ?? undefined;
  let errorCode = searchParams.get('error_code') ?? undefined;

  const code = searchParams.get('code');
  const googleError = searchParams.get('error');
  if (googleError) {
    // The user cancelled or Google refused consent — there is no code to relay,
    // so surface the failure in the wizard instead.
    status = 'error';
    errorCode = googleError === 'access_denied' ? 'access_denied' : 'google_error';
    reason = googleError;
  } else if (code && !isGscScopeGranted(searchParams.get('scope'))) {
    status = 'error';
    errorCode = 'scope_denied';
  } else if (code) {
    status = 'error';
    errorCode = 'relay_unavailable';
  }

  return {
    status,
    reason,
    errorCode,
    sourceId: searchParams.get('source_id') ?? undefined,
    sessionId: searchParams.get('gsc_selection_session_id') ?? undefined,
  };
}

export function gscCallbackOutcomeToQuery(outcome: GscCallbackOutcome): URLSearchParams {
  const query = new URLSearchParams();
  if (outcome.sourceId) query.set('source_id', outcome.sourceId);
  if (outcome.sessionId) query.set('gsc_selection_session_id', outcome.sessionId);
  if (outcome.status) query.set('status', outcome.status);
  if (outcome.reason) query.set('reason', outcome.reason);
  if (outcome.errorCode) query.set('error_code', outcome.errorCode);
  return query;
}

export const GSC_POPUP_CLOSE_POLL_MS = 500;
// Must outlast postMessage delivery from a normally-closing popup: the result
// message and the close race, and the message must win before we call it a
// silent close.
export const GSC_POPUP_RESULT_GRACE_MS = 1500;

// The popup can die without ever posting a result: visibility's callback
// responds with a raw error body (expired code, bad state) instead of
// redirecting back, stranding the popup on visibility's origin — or the user
// simply closes it mid-consent. Without this watcher the opener waits forever
// with no feedback. Polls for the popup closing, then reports a silent close
// unless a result arrived within the grace period. Returns a cancel function.
export function watchGscOAuthPopup(
  popup: Pick<Window, 'closed'>,
  hasReceivedResult: () => boolean,
  onSilentClose: () => void
): () => void {
  let graceTimer: number | undefined;
  const closePoll = window.setInterval(() => {
    if (!popup.closed) {
      return;
    }
    window.clearInterval(closePoll);
    graceTimer = window.setTimeout(() => {
      if (!hasReceivedResult()) {
        onSilentClose();
      }
    }, GSC_POPUP_RESULT_GRACE_MS);
  }, GSC_POPUP_CLOSE_POLL_MS);

  return () => {
    window.clearInterval(closePoll);
    if (graceTimer !== undefined) {
      window.clearTimeout(graceTimer);
    }
  };
}
